import {
  TOPIC_CONTENT_SOURCES,
  type TopicContentSource,
} from "./topic-content-sources";
import { TOPIC_GUIDES, type TopicGuide } from "./topic-guides";

export const BUSINESS_INVOICE_GUIDE = {
  topicId: "business-invoice",
  locale: "ko",
  publicationStatus: "korean_test_preview",
  risk: "R3",
  checkedAt: "2026-07-16",
  jurisdictionLabel: "호주 전역·NSW 분쟁·법원 절차 예시",
  summary:
    "고객이 인보이스를 지급하지 않으면 계약, 견적 승인, 작업완료·납품 증거, 인보이스와 모든 연락 기록을 먼저 한 파일로 정리하세요. 단순 행정 오류인지 작업범위·품질·가격 분쟁인지 확인한 뒤 수정 인보이스와 서면 reminder를 보내고, 합의 가능한 경우 실제로 이행 가능한 payment plan을 문서화합니다. 해결되지 않으면 letter of demand, NSW Small Business Commissioner의 dispute assistance·mediation, debt collection 또는 Local Court를 비용·시간·관계·회수 가능성과 함께 비교하세요. 이자·회수비용·GST adjustment·bad-debt deduction·법원 승소와 실제 회수는 자동 적용되거나 보장되지 않으므로 계약, 세금 방식과 법률 조언을 확인하세요.",
  keyPoints: [
    "계약·견적·variation·납품·작업완료·고객 승인과 인보이스를 한 timeline으로 정리하세요.",
    "인보이스 번호, issue date, due date, supplier·customer details, 제공한 goods·services와 payment methods를 확인하세요.",
    "GST-registered business가 taxable sale을 했다면 ATO tax invoice 요건과 GST 표시를 현재 기준으로 확인하세요.",
    "미지급 원인이 이메일 누락·PO·vendor onboarding·잘못된 계좌·disputed scope인지 먼저 구분하세요.",
    "reminder와 payment plan에는 금액·날짜·이자·fee·분쟁해결 조건을 서면으로 남기세요.",
    "letter of demand는 사실·계약·금액·기한과 다음 단계를 명확히 하되 위협적이거나 허위인 법적 주장을 넣지 마세요.",
    "debt collector를 사용해도 사업자의 법적 책임과 고객 privacy·공정한 추심 의무가 사라지지 않습니다.",
    "법원은 judgment를 줄 수 있어도 debtor가 자산·현금이 없으면 실제 회수에 추가 시간과 비용이 들 수 있습니다.",
    "invoice write-off, GST adjustment와 income-tax bad-debt deduction은 accounting method와 요건을 확인해야 합니다.",
  ],
  sections: [
    {
      id: "verify-contract-and-invoice",
      title: "1. 계약·작업완료·인보이스가 서로 일치하는지 확인하세요",
      body: [
        "signed contract가 없어도 accepted quote, purchase order, email·message, deposit, variation approval와 반복 거래기록이 agreement 증거가 될 수 있습니다. 누가 무엇을 언제 얼마에 제공하기로 했는지, milestone·completion·acceptance, payment term, dispute·interest·recovery-cost 조항과 cancellation을 정리하세요. variation을 구두로 합의했다면 당시 메시지와 작업기록을 보존하세요.",
        "인보이스의 supplier name·ABN, customer details, invoice number·date, description·quantity·price, GST·total, due date와 payment method를 대조하세요. tax invoice라는 제목과 GST 표시가 필요한지는 GST registration과 sale value·type에 따라 다를 수 있습니다. 가짜 ABN·GST나 계약에 없는 fee를 넣지 말고 오류가 있으면 원본 이력을 보존한 채 credit note 또는 corrected invoice 절차를 확인하세요.",
      ],
      checklist: [
        "contract·quote·PO·email과 customer acceptance",
        "scope·variation·delivery·completion evidence",
        "invoice number·issue date·due date",
        "supplier name·ABN·customer details",
        "goods·services description·quantity·price",
        "GST registration·GST amount·tax invoice 요건",
        "payment·interest·collection-cost·dispute terms",
      ],
      sourceIds: [
        "business-invoice-source-invoice",
        "business-invoice-source-contracts",
        "business-invoice-source-ato-tax-invoice",
      ],
    },
    {
      id: "remind-and-fix-errors",
      title: "2. 행정 오류를 해결하고 단계별 서면 reminder를 보내세요",
      body: [
        "due date가 지나면 customer accounts contact에 invoice가 도착했는지, PO·timesheet·delivery confirmation·vendor form·bank verification이 빠졌는지 확인하세요. 전화로 해결하더라도 같은 날 합의 내용을 이메일로 보내고 invoice와 필요한 증빙을 다시 첨부하세요. invoice email이나 bank details가 변조됐을 가능성이 있으면 양쪽 bank와 cyber-fraud 절차를 즉시 확인하세요.",
        "첫 reminder는 invoice number, amount, original due date, payment method와 문의 연락처를 간단히 적고 새로운 response date를 요청하세요. 후속 reminder에는 이전 연락·고객이 제기한 dispute·수정사항·부분 payment와 outstanding balance를 명확히 하세요. 고객이 품질·scope를 다투면 단순 독촉만 반복하지 말고 disputed item과 undisputed amount를 분리해 해결안을 요청하세요.",
      ],
      checklist: [
        "accounts payable 담당자와 invoice 수신 확인",
        "PO·timesheet·delivery·vendor document 누락",
        "bank details·email compromise 가능성",
        "첫·후속 reminder 날짜와 전달증거",
        "customer dispute의 정확한 항목과 금액",
        "부분납부·credit note·outstanding balance",
      ],
      sourceIds: ["business-invoice-source-unpaid"],
    },
    {
      id: "negotiate-or-demand",
      title: "3. payment plan 또는 letter of demand를 사실 중심으로 문서화하세요",
      body: [
        "customer가 일시적인 cash-flow 문제를 인정한다면 deposit 또는 immediate part-payment, instalment amount·date, remaining balance, missed payment 처리와 agreement가 original debt를 대체하는지 서면으로 정하세요. 감당 가능성이 없는 plan을 반복 연장하기보다 financial position과 실제 지급 의사를 확인하고, arrangement가 tax·limitation·security에 미치는 영향은 전문가에게 문의하세요.",
        "협상이 실패하면 letter of demand에 parties, contract·invoice, goods·services, 정확한 outstanding amount, 이전 reminder, payment deadline와 미지급 시 검토할 합법적 다음 단계를 적으세요. 계약상 권리가 확인되지 않은 interest·legal cost를 확정액처럼 요구하거나 criminal action·bankruptcy를 근거 없이 위협하지 마세요. 발송 전 amount와 debtor legal name·address를 다시 확인하고 delivery proof를 보관하세요.",
      ],
      checklist: [
        "즉시 지급액과 instalment schedule",
        "missed payment·interest·fee 처리",
        "original debt·dispute·release에 미치는 영향",
        "letter of demand의 사실·금액·deadline",
        "debtor legal entity와 service address",
        "delivery proof와 response 기록",
        "법률검토가 필요한 security·limitation·insolvency 징후",
      ],
      sourceIds: [
        "business-invoice-source-unpaid",
        "business-invoice-source-contracts",
      ],
    },
    {
      id: "mediate-or-litigate",
      title: "4. mediation·debt collection·NSW Local Court를 비용과 회수 가능성으로 비교하세요",
      body: [
        "NSW small business dispute라면 NSW Small Business Commissioner의 dispute support와 mediation 범위·fee·상대방 참여 여부를 확인하세요. mediation은 독립 mediator가 agreement를 돕지만 누가 옳은지 결정하거나 payment를 강제하는 절차는 아닙니다. ongoing customer·supplier 관계나 scope dispute가 있으면 court 전 해결 가능성을 검토하세요.",
        "debt collection agency·lawyer를 사용할 때 commission·legal cost·업무범위, complaint handling, privacy와 브랜드 영향을 서면으로 확인하세요. outsourced collector의 행위가 항상 적법하다고 가정하지 마세요.",
        "NSW Local Court civil claim은 money 또는 goods claim을 다루며 금액에 따라 Small Claims·General Division이 달라질 수 있습니다. filing fee, limitation, correct defendant, statement of claim, service, defence·default judgment, hearing와 enforcement 비용을 법률 조언과 현재 court 안내에서 확인하세요. 승소해도 실제 payment가 자동으로 들어오는 것은 아닙니다.",
      ],
      checklist: [
        "NSW Small Business Commissioner 관할·fee·mediation suitability",
        "상대방 legal entity·주소·지급능력",
        "collector·lawyer commission·cost·privacy terms",
        "claim amount와 Local Court division",
        "limitation·filing·service·response deadline",
        "judgment 후 enforcement 비용·자산 가능성",
        "commercial relationship과 settlement value",
      ],
      sourceIds: [
        "business-invoice-source-nsw-disputes",
        "business-invoice-source-local-court",
      ],
    },
    {
      id: "record-tax-and-prevent",
      title: "5. 회수 결과를 장부·GST·세금에 반영하고 재발 방지 조건을 바꾸세요",
      body: [
        "payment, partial settlement, credit note, write-off, collector fee와 legal cost를 invoice ledger와 accounting system에 기록하세요. GST를 이미 보고한 debt가 나중에 unrecoverable이 되거나 recovered되는 경우 adjustment가 필요한지 accounting method와 ATO rules를 확인하세요. income-tax bad-debt deduction도 단순 연체만으로 자동 인정되는 것이 아니며 debt가 assessable income에 포함됐는지, 실제 bad로 판단하고 해당 income year에 written off했는지 등 요건을 tax professional과 확인하세요.",
        "재발을 줄이기 위해 credit check, deposit·milestone billing, shorter payment term, PO before work, signed variation, automatic reminder, late-payment clause와 stop-work right를 contract에 명확히 하세요. customer risk와 industry practice에 맞게 조건을 정하고 unfair term·consumer law·security-interest 등록이 필요한지는 독립 조언을 받으세요.",
      ],
      sourceIds: [
        "business-invoice-source-invoice",
        "business-invoice-source-ato-tax-invoice",
        "business-invoice-source-ato-bad-debt",
      ],
    },
  ],
  faqs: [
    {
      id: "business-invoice-no-contract",
      question: "서명한 계약서가 없으면 미수금을 받을 수 없나요?",
      answer:
        "반드시 그렇지는 않습니다. accepted quote, PO, email·message, deposit, delivery와 work acceptance가 agreement 증거가 될 수 있습니다. parties·scope·price·due date를 정리하고 분쟁 규모가 크면 법률 조언을 받으세요.",
      sourceIds: ["business-invoice-source-contracts"],
    },
    {
      id: "business-invoice-tax-invoice",
      question: "모든 인보이스에 GST와 ‘Tax Invoice’를 표시해야 하나요?",
      answer:
        "GST registration, taxable sale와 invoice value에 따라 요건이 다릅니다. GST에 등록하지 않았다면 GST를 청구하지 말고, 등록 사업자는 ATO의 current tax invoice 필수정보를 확인하세요.",
      sourceIds: [
        "business-invoice-source-invoice",
        "business-invoice-source-ato-tax-invoice",
      ],
    },
    {
      id: "business-invoice-late-interest",
      question: "지급이 늦으면 원하는 이자를 추가해도 되나요?",
      answer:
        "자동으로 가능한 것은 아닙니다. 계약의 late-payment·interest 조항, 적용 법률과 계산방식을 확인해야 합니다. 합의되지 않은 interest·collection cost를 임의로 확정 청구하지 말고 법률 조언을 받으세요.",
      sourceIds: [
        "business-invoice-source-contracts",
        "business-invoice-source-unpaid",
      ],
    },
    {
      id: "business-invoice-part-payment",
      question: "고객이 일부만 지급하겠다고 합니다.",
      answer:
        "받을지 판단하기 전에 payment가 단순 part-payment인지 full and final settlement인지 서면으로 명확히 하세요. remaining balance, instalment dates와 missed payment 처리를 기록하고 권리 포기 표현은 이해한 뒤 사용하세요.",
      sourceIds: ["business-invoice-source-unpaid"],
    },
    {
      id: "business-invoice-disputed-work",
      question: "고객이 작업 품질이나 scope를 이유로 전액 지급을 거부합니다.",
      answer:
        "계약·variation·completion·defect notice와 고객 승인 기록을 대조하고 disputed item과 undisputed amount를 구분해 달라고 요청하세요. 수정·discount·mediation과 법적 청구의 비용을 비교하세요.",
      sourceIds: [
        "business-invoice-source-contracts",
        "business-invoice-source-nsw-disputes",
      ],
    },
    {
      id: "business-invoice-mediation-binding",
      question: "mediation에 가면 상대방에게 지급을 명령할 수 있나요?",
      answer:
        "일반적으로 mediator는 결정을 내리거나 강제하지 않고 parties의 agreement를 돕습니다. 합의가 되면 조건을 서면으로 정하고, enforceability가 중요하면 법률 조언을 받으세요.",
      sourceIds: ["business-invoice-source-nsw-disputes"],
    },
    {
      id: "business-invoice-small-claims",
      question: "미지급 인보이스는 NSW Small Claims Division에 바로 제출하면 되나요?",
      answer:
        "claim amount, correct defendant, limitation, service address와 evidence를 먼저 확인해야 합니다. filing·service·hearing·enforcement 비용과 mediation 가능성을 비교하고 현재 Local Court 절차를 따르세요.",
      sourceIds: ["business-invoice-source-local-court"],
    },
    {
      id: "business-invoice-court-guarantee",
      question: "법원에서 이기면 돈을 바로 받을 수 있나요?",
      answer:
        "아닙니다. judgment 후에도 debtor가 자발적으로 지급하지 않으면 별도 enforcement가 필요할 수 있고, 자산·현금이 없으면 회수하지 못하거나 비용이 늘 수 있습니다.",
      sourceIds: ["business-invoice-source-local-court"],
    },
    {
      id: "business-invoice-writeoff",
      question: "오래된 미수금을 장부에서 지우면 GST와 세금도 자동으로 줄어드나요?",
      answer:
        "자동이 아닙니다. GST accounting method, 이전 BAS 처리, debt recovery와 income-tax bad-debt 요건을 확인해야 합니다. write-off와 tax adjustment를 같은 것으로 보지 말고 accountant·registered tax agent에게 확인하세요.",
      sourceIds: [
        "business-invoice-source-ato-tax-invoice",
        "business-invoice-source-ato-bad-debt",
      ],
    },
    {
      id: "business-invoice-customer-insolvent",
      question: "고객 회사가 insolvency나 liquidation에 들어간 것 같습니다.",
      answer:
        "추가 work를 멈출 권리, secured interest, proof of debt와 administrator·liquidator deadline을 즉시 확인하세요. 일반 reminder나 court claim만 계속하지 말고 accountant와 insolvency·legal advice를 받으세요.",
      sourceIds: [
        "business-invoice-source-unpaid",
        "business-invoice-source-contracts",
      ],
    },
  ],
  sourceIds: [
    "business-invoice-source-invoice",
    "business-invoice-source-unpaid",
    "business-invoice-source-contracts",
    "business-invoice-source-nsw-disputes",
    "business-invoice-source-local-court",
    "business-invoice-source-ato-tax-invoice",
    "business-invoice-source-ato-bad-debt",
  ],
  additionalChecks: [
    "debtor legal name·ABN/ACN, address, contract party와 insolvency status를 demand·claim 전에 확인하세요.",
    "interest, collection cost, stop-work와 security 권리는 실제 contract와 적용 법률에서 확인하세요.",
    "NSW mediation·Local Court의 current jurisdiction, claim limit, fee, form, limitation과 service 규칙을 제출 전에 확인하세요.",
    "debt collection provider의 licence·fee·privacy·complaint terms와 outsourced conduct 책임을 확인하세요.",
    "GST adjustment와 bad-debt deduction은 accounting method, prior reporting와 write-off evidence를 ATO·tax professional에게 확인하세요.",
    "large debt, insolvency signs, counterclaim 또는 customer asset movement가 있으면 즉시 독립 법률 조언을 받으세요.",
  ],
  disclaimer:
    "이 콘텐츠는 호주의 사업 인보이스와 미수금 대응에 관한 R3 테스트 미리보기 생활정보이며 법률·세무·회계 자문이 아닙니다. 계약 권리, interest·recovery cost, mediation 합의, 법원 승소·집행, GST adjustment 또는 bad-debt deduction을 개인 사업에 확정하지 않습니다. 계약, entity, 증거, limitation, insolvency와 accounting method에 따라 결과가 달라지므로 business.gov.au, NSW Small Business Commissioner, NSW Local Court, ATO와 필요한 전문가의 최신 안내를 확인하세요.",
} as const satisfies TopicGuide;

export const BUSINESS_INVOICE_SOURCES = [
  {
    sourceId: "business-invoice-source-invoice",
    topicId: "business-invoice",
    title: "How to invoice",
    publisher: "business.gov.au",
    url: "https://business.gov.au/finance/payments-and-invoicing/how-to-invoice",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "2025년 12월 11일 갱신된 Australian Government business.gov.au 본문에서 invoice 목적·필수정보·payment terms·GST tax invoice 연결 확인",
    reviewStatus: "live_verified",
    claimScope: "호주 small business invoice 작성, 지급조건과 기록관리의 일반 기준",
    followUp: "GST status·sale value별 current ATO tax invoice requirements와 eInvoicing option 재확인",
  },
  {
    sourceId: "business-invoice-source-unpaid",
    topicId: "business-invoice",
    title: "Dealing with unpaid invoices",
    publisher: "business.gov.au",
    url: "https://business.gov.au/finance/payments-and-invoicing/dealing-with-unpaid-invoices",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Australian Government business.gov.au 공식 본문에서 invoice 확인, reminder, payment plan, letter of demand, debt collection·legal action 순서 확인",
    reviewStatus: "live_verified",
    claimScope: "미지급 사업 인보이스의 단계별 연락·협상·외부 회수 절차",
    followUp: "개별 contract, state court·tribunal, limitation과 debtor insolvency status 재확인",
  },
  {
    sourceId: "business-invoice-source-contracts",
    topicId: "business-invoice",
    title: "Prepare a contract",
    publisher: "business.gov.au",
    url: "https://business.gov.au/people/contractors/prepare-a-contract",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "Australian Government business.gov.au 공식 본문에서 scope·payment·variation·dispute·termination을 서면 contract에 명확히 할 필요 확인",
    reviewStatus: "live_verified",
    claimScope: "business contract의 작업범위·payment terms·variation와 dispute evidence",
    followUp: "industry-specific law, unfair contract terms와 security·insurance clauses는 법률 조언으로 재확인",
  },
  {
    sourceId: "business-invoice-source-nsw-disputes",
    topicId: "business-invoice",
    title: "Resolving business disputes",
    publisher: "NSW Small Business Commissioner",
    url: "https://www.smallbusiness.nsw.gov.au/get-help/resolving-business-disputes",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "NSW Small Business Commissioner 공식 본문에서 negotiation, dispute support·mediation의 역할과 court 전 해결 경로 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW small business-to-business dispute의 정보·assistance·mediation 시작점",
    followUp: "현재 mediation fee, eligibility, 상대방 참여와 excluded dispute categories 재확인",
  },
  {
    sourceId: "business-invoice-source-local-court",
    topicId: "business-invoice",
    title: "Civil cases",
    publisher: "Local Court of New South Wales",
    url: "https://localcourt.nsw.gov.au/types-of-cases/civil-cases.html",
    trust: 5,
    jurisdiction: "nsw",
    checkedAt: "2026-07-16",
    verificationMethod:
      "NSW Local Court 공식 본문에서 money·goods civil claim, Small Claims·General Division, statement of claim와 court process 확인",
    reviewStatus: "live_verified",
    claimScope: "NSW civil debt claim의 court jurisdiction과 기본 제기·대응·집행 경로",
    followUp: "current monetary limits, filing fee, forms, service, limitation와 enforcement 절차 재확인",
  },
  {
    sourceId: "business-invoice-source-ato-tax-invoice",
    topicId: "business-invoice",
    title: "Tax invoices",
    publisher: "Australian Taxation Office",
    url: "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/tax-invoices",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "ATO 공식 tax invoice URL과 publisher를 확인했으나 이 실행환경에서 본문이 403으로 제한되어 business.gov.au의 ATO 연결 설명과 기존 공식 요건 범위만 교차확인",
    reviewStatus: "official_access_limited",
    claimScope: "GST-registered supplier의 tax invoice 제목·정보와 taxable sale 표시의 공식 상세기준",
    followUp: "발행 직전 ATO 원문에서 $1,000 기준별 필수정보, recipient-created invoice와 correction 요건 직접 재확인",
  },
  {
    sourceId: "business-invoice-source-ato-bad-debt",
    topicId: "business-invoice",
    title: "Bad debts",
    publisher: "Australian Taxation Office",
    url: "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/income-and-deductions-for-business/deductions/deductions-for-operating-expenses/bad-debts",
    trust: 5,
    jurisdiction: "australia",
    checkedAt: "2026-07-16",
    verificationMethod:
      "ATO 공식 bad-debt URL과 publisher를 확인했으나 이 실행환경에서 본문 접근이 제한되어 tax treatment는 단정하지 않고 전문 확인 대상으로 제한",
    reviewStatus: "official_access_limited",
    claimScope: "business bad-debt income-tax deduction과 write-off 시점·assessable-income 요건의 공식 상세기준",
    followUp: "해당 income year의 ATO 원문, GST accounting method, prior BAS·income inclusion과 written-off evidence를 registered tax agent와 재확인",
  },
] as const satisfies readonly TopicContentSource[];

const mutableGuides = TOPIC_GUIDES as unknown as TopicGuide[];
if (!mutableGuides.some((guide) => guide.topicId === BUSINESS_INVOICE_GUIDE.topicId)) {
  mutableGuides.push(BUSINESS_INVOICE_GUIDE);
}

const mutableSources = TOPIC_CONTENT_SOURCES as unknown as TopicContentSource[];
for (const source of BUSINESS_INVOICE_SOURCES) {
  if (!mutableSources.some((existing) => existing.sourceId === source.sourceId)) {
    mutableSources.push(source);
  }
}
